import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Coins, Star, Crown, Eye, TrendUp, ChartLine, ChatCircleText } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import BookingChat from '../components/BookingChat';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import CenterInviteBanner from '../components/CenterInviteBanner';
import ProOnboardingBanner from '../components/ProOnboardingBanner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const ProfessionalDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [chatBookingId, setChatBookingId] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip fetching professional-only endpoints if pending/rejected (will 403)
    if (user && user.professional_status && user.professional_status !== 'approved') {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [bookingsRes, subRes, analyticsRes] = await Promise.all([
        api.get('/api/bookings/professional').catch(() => ({ data: [] })),
        api.get('/api/subscriptions/status').catch(() => ({ data: null })),
        api.get('/api/analytics/professional').catch(() => ({ data: null })),
      ]);
      setBookings(bookingsRes.data);
      setSubscription(subRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-sky-100 text-sky-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Show pending approval banner if account is not approved
  if (user && user.professional_status === 'pending') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-20 space-y-6">
          <EmailVerificationBanner />
          <Card className="neumorphism rounded-2xl p-8 text-center" data-testid="pending-approval-banner">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Star size={40} className="text-yellow-600" weight="duotone" />
            </div>
            <h1 className="font-heading font-semibold text-3xl mb-4">Account in Attesa di Approvazione</h1>
            <p className="text-foreground/70 mb-4">
              Il tuo account professionista è in attesa di verifica da parte del nostro team.
              Verrai contattato via email non appena l'approvazione sarà completata.
            </p>
            <p className="text-sm text-foreground/60">
              Tempo medio di approvazione: 24-48 ore lavorative
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (user && user.professional_status === 'rejected') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-20">
          <Card className="neumorphism rounded-2xl p-8 text-center border-l-4 border-l-red-500" data-testid="rejected-banner">
            <h1 className="font-heading font-semibold text-3xl mb-4">Account Non Approvato</h1>
            <p className="text-foreground/70">
              Il tuo account non è stato approvato. Contatta il supporto per ulteriori informazioni.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  const revenue = analytics?.revenue_total || 0;
  const revenueThisMonth = analytics?.revenue_this_month || 0;
  const viewsData = analytics?.views_last_30_days || [];
  const bookingsData = Object.entries(analytics?.bookings_by_status || {}).map(([status, count]) => ({
    status,
    count,
  }));

  return (
    <div className="min-h-screen bg-background">

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-heading font-semibold text-4xl text-foreground mb-2" data-testid="prof-dashboard-title">
            Dashboard Professionista
          </h1>
          <p className="text-foreground/70">Benvenuto, {user?.name}</p>
        </div>

        <div className="mb-8">
          <EmailVerificationBanner />
        </div>

        <CenterInviteBanner />

        <ProOnboardingBanner />

        {subscription?.is_premium ? (
          <Card className="neumorphism rounded-2xl p-6 mb-8 bg-gradient-to-r from-accent/10 to-primary/10 border-l-4 border-l-accent" data-testid="premium-active-banner">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                  <Crown size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-lg">Sei Premium!</p>
                  <p className="text-sm text-foreground/70">
                    Piano {subscription.plan === 'yearly' ? 'Annuale' : 'Mensile'} • Scade{' '}
                    {new Date(subscription.expires_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
              <Link to="/premium">
                <Button variant="outline" className="rounded-full" data-testid="manage-premium-btn">
                  Gestisci
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <Card className="neumorphism rounded-2xl p-6 mb-8 bg-gradient-to-r from-accent/5 to-primary/5" data-testid="premium-upgrade-banner">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                  <Crown size={24} className="text-accent" weight="duotone" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-lg">Passa a Premium</p>
                  <p className="text-sm text-foreground/70">
                    Ottieni visibilità prioritaria per soli €19/mese
                  </p>
                </div>
              </div>
              <Link to="/premium">
                <Button className="rounded-full bg-accent hover:bg-accent/90" data-testid="upgrade-premium-btn">
                  Scopri Premium
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="neumorphism rounded-2xl p-5" data-testid="prof-stats-views">
            <div className="flex items-center gap-3 mb-2">
              <Eye size={20} className="text-primary" weight="duotone" />
              <p className="text-xs text-foreground/60 uppercase tracking-wider">Visualizzazioni</p>
            </div>
            <p className="text-3xl font-heading font-bold">{analytics?.views_count || 0}</p>
          </Card>

          <Card className="neumorphism rounded-2xl p-5" data-testid="prof-stats-bookings">
            <div className="flex items-center gap-3 mb-2">
              <Calendar size={20} className="text-accent" weight="duotone" />
              <p className="text-xs text-foreground/60 uppercase tracking-wider">Prenotazioni</p>
            </div>
            <p className="text-3xl font-heading font-bold">{analytics?.bookings_count || 0}</p>
          </Card>

          <Card className="neumorphism rounded-2xl p-5" data-testid="prof-stats-earnings">
            <div className="flex items-center gap-3 mb-2">
              <Coins size={20} className="text-accent" weight="duotone" />
              <p className="text-xs text-foreground/60 uppercase tracking-wider">Guadagni Totali</p>
            </div>
            <p className="text-3xl font-heading font-bold">€{revenue.toFixed(0)}</p>
            <p className="text-xs text-foreground/60 mt-1">€{revenueThisMonth.toFixed(0)} questo mese</p>
          </Card>

          <Card className="neumorphism rounded-2xl p-5" data-testid="prof-stats-rating">
            <div className="flex items-center gap-3 mb-2">
              <Star size={20} className="text-yellow-500" weight="duotone" />
              <p className="text-xs text-foreground/60 uppercase tracking-wider">Rating</p>
            </div>
            <p className="text-3xl font-heading font-bold">
              {(analytics?.average_rating || 0).toFixed(1)}
            </p>
            <p className="text-xs text-foreground/60 mt-1">
              {analytics?.reviews_count || 0} recensioni
            </p>
          </Card>
        </div>

        <Card className="neumorphism rounded-2xl p-6">
          <Tabs defaultValue="analytics">
            <TabsList data-testid="prof-dashboard-tabs">
              <TabsTrigger value="analytics">
                <ChartLine size={16} className="mr-1" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="bookings">Prenotazioni</TabsTrigger>
            </TabsList>

            <TabsContent value="analytics" className="mt-6" data-testid="analytics-tab-content">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="neumorphism rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendUp size={20} className="text-primary" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Visite Profilo (30 giorni)</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={viewsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(d) => d.slice(5)}
                        interval={4}
                      />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="views"
                        stroke="#1565C0"
                        strokeWidth={2}
                        dot={{ fill: '#1565C0', r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="neumorphism rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar size={20} className="text-accent" weight="duotone" />
                    <h3 className="font-heading font-medium text-lg">Prenotazioni per Stato</h3>
                  </div>
                  {bookingsData.length === 0 ? (
                    <div className="flex items-center justify-center h-[250px] text-foreground/60">
                      Nessuna prenotazione ancora
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={bookingsData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#2CA9E1" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="bookings" className="mt-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-foreground/70">Nessuna prenotazione ancora</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="prof-bookings-list">
                  {bookings.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className="border border-border rounded-xl p-4 hover:shadow-md transition-shadow"
                      data-testid={`prof-booking-${booking.booking_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-lg mb-2">{booking.service_type}</p>
                          <p className="text-sm text-foreground/70 mb-1">
                            Data: {new Date(booking.date).toLocaleDateString('it-IT')} - {booking.time}
                          </p>
                          <p className="text-sm text-foreground/70">Importo: {booking.amount}€</p>
                          {booking.notes && (
                            <p className="text-sm text-foreground/70 mt-2">Note: {booking.notes}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <Badge className={getStatusColor(booking.status)}>{booking.status}</Badge>
                          <Badge
                            className={
                              booking.payment_status === 'paid'
                                ? 'bg-sky-100 text-sky-800'
                                : 'bg-gray-100 text-gray-800'
                            }
                          >
                            {booking.payment_status}
                          </Badge>
                          <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => setChatBookingId(booking.booking_id)} data-testid={`prof-open-chat-${booking.booking_id}`}>
                            <ChatCircleText size={14} className="mr-1" weight="duotone" /> Chat
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      {chatBookingId && (
        <BookingChat bookingId={chatBookingId} onClose={() => setChatBookingId(null)} />
      )}
    </div>
  );
};

export default ProfessionalDashboard;
